const NBSP = '\u00A0';
export const NNBSP = '\u202F';

export interface PreventWidowsPlOptions {
  /** Default glue used by most rules. */
  nbsp?: string;
  /** Separate glue for grouped digits, e.g. 1 234 567. Defaults to nbsp. */
  numberGroupGlue?: string;
  /** Also glue short 2–3-letter words like: na, do, od, po, za, we, ze, nie, że, czy, ale. */
  aggressiveShortWords?: boolean;
  /** Extend the built-in dictionary of one-letter words. */
  extraSingleLetterWords?: readonly string[];
  /** Extend the built-in dictionary of short words used in aggressive mode. */
  extraShortWords?: readonly string[];
  /** Extend the dictionary of abbreviations that should stay with the following token. */
  extraPrefixAbbreviations?: readonly string[];
  /** Extend the dictionary of units/currencies/symbols bound to numbers. */
  extraUnits?: readonly string[];
}

const DEFAULT_SINGLE_LETTER_WORDS = new Set(['a', 'i', 'o', 'u', 'w', 'z']);

const DEFAULT_SHORT_WORDS = new Set([
  'na',
  'do',
  'od',
  'po',
  'za',
  'we',
  'ze',
  'nie',
  'że',
  'czy',
  'ale',
]);

const DEFAULT_PREFIX_ABBREVIATIONS = new Set([
  // titles / honorifics
  'dr',
  'dr.',
  'mgr',
  'mgr.',
  'prof',
  'prof.',
  'inż',
  'inż.',
  'hab',
  'hab.',
  'lek',
  'lek.',
  'śp',
  'śp.',
  'ks',
  'ks.',
  'bp',
  'bp.',
  'abp',
  'abp.',
  'gen',
  'gen.',
  'mjr',
  'mjr.',
  'płk',
  'płk.',
  'ppłk',
  'ppłk.',

  // discourse / sentence abbreviations
  'np',
  'np.',
  'tzw',
  'tzw.',
  'tzn',
  'tzn.',
  'tj',
  'tj.',
  'm.in.',
  'itd',
  'itd.',
  'itp',
  'itp.',
  'dn',
  'dn.',
  'godz',
  'godz.',

  // legal / editorial / bibliographic markers
  'nr',
  'pkt',
  'ppkt',
  'lit',
  'lit.',
  'rozdz',
  'rozdz.',
  'str',
  'str.',
  's',
  's.',
  't',
  't.',
  'cz',
  'cz.',
  'tab',
  'tab.',
  'rys',
  'rys.',
  'art',
  'art.',
  'ust',
  'ust.',
  'par',
  'par.',
  '§',
  'al',
  'al.',
  'ul',
  'ul.',
  'os',
  'os.',
  'dz',
  'dz.',
  'poz',
  'poz.',
]);

const DEFAULT_UNITS = new Set([
  '%',
  '‰',
  '°',
  '°c',
  '°f',
  '°k',

  // metric / technical
  'km',
  'm',
  'dm',
  'cm',
  'mm',
  'μm',
  'µm',
  'nm',
  'pm',
  'm2',
  'm3',
  'km2',
  'km3',
  'cm2',
  'cm3',
  'ha',
  'kg',
  'g',
  'mg',
  'μg',
  'µg',
  'ng',
  't',
  'l',
  'ml',
  'dl',
  'hl',
  's',
  'sek',
  'sek.',
  'min',
  'min.',
  'h',
  'godz',
  'godz.',
  'd',
  'tydz',
  'tydz.',
  'hz',
  'khz',
  'mhz',
  'ghz',
  'w',
  'kw',
  'mw',
  'gw',
  'j',
  'kj',
  'mj',
  'n',
  'pa',
  'kpa',
  'mpa',
  'bar',
  'v',
  'kv',
  'a',
  'ma',
  'ω',
  'kω',
  'mω',
  'b',
  'kb',
  'mb',
  'gb',
  'tb',
  'pb',
  'px',
  'dpi',

  // currencies
  'zł',
  'gr',
  'pln',
  'eur',
  'usd',
  'chf',
  'gbp',
  '€',
  '$',
  '£',
]);

const MONTHS = new Set([
  'stycznia',
  'lutego',
  'marca',
  'kwietnia',
  'maja',
  'czerwca',
  'lipca',
  'sierpnia',
  'września',
  'października',
  'listopada',
  'grudnia',
  'i',
  'ii',
  'iii',
  'iv',
  'v',
  'vi',
  'vii',
  'viii',
  'ix',
  'x',
  'xi',
  'xii',
]);

const YEAR_ABBREVIATIONS = new Set([
  'r',
  'r.',
  'roku',
  'br',
  'br.',
  'bm',
  'bm.',
]);

const LEGAL_OR_NUMBER_PREFIXES = new Set([
  'nr',
  'pkt',
  'ppkt',
  'lit',
  'lit.',
  'rozdz',
  'rozdz.',
  'str',
  'str.',
  's',
  's.',
  't',
  't.',
  'cz',
  'cz.',
  'tab',
  'tab.',
  'rys',
  'rys.',
  'art',
  'art.',
  'ust',
  'ust.',
  'par',
  'par.',
  '§',
  'poz',
  'poz.',
]);

const LEADING_PUNCT_RE = /^[([{"'„«‹]+/u;
const TRAILING_DECORATION_RE = /[),;:!?…"'»”›\]}]+$/u;
const TERMINAL_DOT_RE = /\.$/u;

function normalizeToken(token: string): string {
  return token
    .replace(LEADING_PUNCT_RE, '')
    .replace(TRAILING_DECORATION_RE, '');
}

function normalizeWord(token: string): string {
  return normalizeToken(token).replace(TERMINAL_DOT_RE, '');
}

function lowerPl(value: string): string {
  return value.toLocaleLowerCase('pl-PL');
}

function isSpaceLikeSeparator(separator: string): boolean {
  return /^[ \t]+$/u.test(separator);
}

function isNumberToken(token: string): boolean {
  return /^[+-]?\d+(?:[.,]\d+)?$/u.test(normalizeToken(token));
}

function isIntegerToken(token: string): boolean {
  return /^\d+$/u.test(normalizeToken(token));
}

function isYearToken(token: string): boolean {
  return /^\d{4}$/u.test(normalizeToken(token));
}

function isFullNumericDateToken(token: string): boolean {
  const normalized = normalizeToken(token);
  return (
    /^(?:\d{1,2}[.-]){2}\d{2,4}$/u.test(normalized) ||
    /^\d{4}-\d{2}-\d{2}$/u.test(normalized)
  );
}

function isDayToken(token: string): boolean {
  return /^(?:[1-9]|[12]\d|3[01])$/u.test(normalizeToken(token));
}

function isThreeDigitGroup(token: string): boolean {
  return /^\d{3}$/u.test(normalizeToken(token));
}

function isInitial(token: string): boolean {
  return /^\p{Lu}\.$/u.test(normalizeToken(token));
}

/**
 * Deliberately conservative to avoid false positives like:
 * "A. To jest test."
 *
 * If you need to support very short surnames like "Li" / "Xu",
 * loosen {2,} to {1,}.
 */
function looksLikeNameWord(token: string): boolean {
  return /^\p{Lu}[\p{L}'’.-]{2,}$/u.test(normalizeToken(token));
}

function isDottedAbbreviationPart(token: string): boolean {
  return /^[\p{L}]{1,3}\.$/u.test(normalizeToken(token));
}

function isUnitToken(token: string, units: ReadonlySet<string>): boolean {
  const raw = normalizeToken(token);
  const lower = lowerPl(raw);
  return units.has(raw) || units.has(lower);
}

function isMonthToken(token: string): boolean {
  return MONTHS.has(lowerPl(normalizeWord(token)));
}

function isYearAbbreviationToken(token: string): boolean {
  const normalized = lowerPl(normalizeToken(token));
  return YEAR_ABBREVIATIONS.has(normalized);
}

function resolveOptions(options: PreventWidowsPlOptions) {
  const singleLetterWords = new Set(DEFAULT_SINGLE_LETTER_WORDS);
  const shortWords = new Set(DEFAULT_SHORT_WORDS);
  const prefixAbbreviations = new Set(DEFAULT_PREFIX_ABBREVIATIONS);
  const units = new Set(DEFAULT_UNITS);

  for (const word of options.extraSingleLetterWords ?? []) {
    singleLetterWords.add(lowerPl(word));
  }

  for (const word of options.extraShortWords ?? []) {
    shortWords.add(lowerPl(word));
  }

  for (const abbreviation of options.extraPrefixAbbreviations ?? []) {
    prefixAbbreviations.add(lowerPl(abbreviation));
  }

  for (const unit of options.extraUnits ?? []) {
    units.add(unit);
    units.add(lowerPl(unit));
  }

  return {
    nbsp: options.nbsp ?? NBSP,
    numberGroupGlue: options.numberGroupGlue ?? options.nbsp ?? NBSP,
    aggressiveShortWords: options.aggressiveShortWords ?? false,
    singleLetterWords,
    shortWords,
    prefixAbbreviations,
    units,
  };
}

function whichGlue(
  prevPrevToken: string,
  prevSeparator: string,
  prevToken: string,
  nextToken: string,
  options: ReturnType<typeof resolveOptions>,
): string | null {
  const prev = normalizeToken(prevToken);
  const next = normalizeToken(nextToken);
  const prevLower = lowerPl(prev);
  const prevWord = lowerPl(normalizeWord(prevToken));
  const prevHasTerminalDot = TERMINAL_DOT_RE.test(prev);

  if (!prev || !next) {
    return null;
  }

  // a, i, o, u, w, z
  if (!prevHasTerminalDot && options.singleLetterWords.has(prevWord)) {
    return options.nbsp;
  }

  // na, do, od, po, za, we, ze, nie, że, czy, ale...
  if (
    !prevHasTerminalDot &&
    options.aggressiveShortWords &&
    options.shortWords.has(prevWord)
  ) {
    return options.nbsp;
  }

  // dr Kowalski, np. przykład, art. 5, nr 2, § 7...
  if (
    options.prefixAbbreviations.has(prevLower) ||
    options.prefixAbbreviations.has(prevWord) ||
    LEGAL_OR_NUMBER_PREFIXES.has(prevLower) ||
    LEGAL_OR_NUMBER_PREFIXES.has(prevWord)
  ) {
    return options.nbsp;
  }

  // J. K. Kowalski / J. Kowalski
  if (
    isInitial(prevToken) &&
    (isInitial(nextToken) || looksLikeNameWord(nextToken))
  ) {
    return options.nbsp;
  }

  // m. in. / j. w. / Dz. U.
  if (
    isDottedAbbreviationPart(prevToken) &&
    isDottedAbbreviationPart(nextToken)
  ) {
    return options.nbsp;
  }

  // m. in. przykład / Dz. U. z ...
  if (
    isDottedAbbreviationPart(prevToken) &&
    prevSeparator === options.nbsp &&
    isDottedAbbreviationPart(prevPrevToken)
  ) {
    return options.nbsp;
  }

  // 1 234 567
  if (
    isIntegerToken(prevToken) &&
    isThreeDigitGroup(nextToken) &&
    normalizeToken(prevToken).length <= 3
  ) {
    return options.numberGroupGlue;
  }

  // 20 kg / 50 zł / 30 %
  if (isNumberToken(prevToken) && isUnitToken(nextToken, options.units)) {
    return options.nbsp;
  }

  // 12 marca / 27 I / marca 2024 / 2024 r. / 27.01.2024 r.
  if (isDayToken(prevToken) && isMonthToken(nextToken)) {
    return options.nbsp;
  }

  if (isMonthToken(prevToken) && isYearToken(nextToken)) {
    return options.nbsp;
  }

  if (
    (isYearToken(prevToken) || isFullNumericDateToken(prevToken)) &&
    isYearAbbreviationToken(nextToken)
  ) {
    return options.nbsp;
  }

  return null;
}

export function preventWidowsPl(
  value: string | null | undefined,
  options: PreventWidowsPlOptions = {},
): string {
  if (!value) {
    return '';
  }

  const resolved = resolveOptions(options);
  const parts = value.split(/([ \t\r\n]+)/u);

  for (let i = 1; i < parts.length - 1; i += 2) {
    const separator = parts[i];

    // Never glue across an explicit newline.
    if (!isSpaceLikeSeparator(separator)) {
      continue;
    }

    const glue = whichGlue(
      parts[i - 3] ?? '',
      parts[i - 2] ?? '',
      parts[i - 1],
      parts[i + 1],
      resolved,
    );

    if (glue) {
      parts[i] = glue;
    }
  }

  return parts.join('');
}

// compatibility with the old name
export const preventSingleLetterWidows = preventWidowsPl;
